import {
  useState,
  useCallback,
  type SetStateAction,
  type Dispatch,
  type ReactNode,
} from "react";
import queryString from "query-string";
import { addDays, startOfDay } from "date-fns";
import {
  type AccidentReport,
  type Vehicle,
  SignatureStatus,
  type PageTwo,
} from "#accident-statement/schema";
import { useAdminClient } from "../../api/AdminClient";
import { LoadingText } from "@naf/teamscheme";
import Bytes from "../../../shared/Bytes";
import type { PaginationResult } from "../../../shared/models/PaginationResult";

type Primitive = string | number | boolean;
type Entry = Object | Primitive | null | undefined;
type Object = { [data: string]: Entry | Entry[] };

function isPrimitive(data: Entry | Entry[]): data is Primitive {
  return Object(data) !== data;
}

function flattenObject(data: Entry | Entry[]) {
  const result: { [key: string]: Primitive } = {};

  function recurse(cur: Entry | Entry[], prop: string) {
    if (isPrimitive(cur)) {
      if (cur !== null && cur !== undefined) {
        result[prop] = cur;
      }
    } else if (Array.isArray(cur)) {
      const l = cur.length;

      for (let i = 0; i < l; i++) {
        recurse(cur[i], `${prop}[${i}]`);
      }
    } else {
      for (const p in cur) {
        const next = cur[p];
        recurse(next, prop ? `${prop}.${p}` : p);
      }
    }
  }

  recurse(data, "");

  return result;
}

enum ExportOption {
  From = "From",
  To = "To",
  Limit = "Limit",
}

interface ExportOptionDefinition {
  id: ExportOption;
  label: string;
  type: "checkbox" | "date" | "number";
  default?: string;
}

const EXPORT_OPTIONS: ExportOptionDefinition[] = [
  {
    id: ExportOption.From,
    label: "From Date",
    type: "date",
    default: formatDateInputValue(addDays(startOfDay(new Date()), -1)),
  },
  {
    id: ExportOption.To,
    label: "To Date",
    type: "date",
    default: formatDateInputValue(addDays(startOfDay(new Date()), 1)),
  },
  {
    id: ExportOption.Limit,
    label: "Page Size",
    type: "number",
    default: "100",
  },
];

function getExportData(
  report: AccidentReport,
  options?: Partial<{ [key in ExportOption]: string | boolean }> & {
    vehicleSections?: Partial<{ [key in keyof Vehicle]: boolean }>;
    commonSections?: Partial<{ [key in keyof AccidentReport]: boolean }>;
    pageTwoSections?: Partial<{ [key in keyof PageTwo]: boolean }>;
  },
) {
  const commonSectionColumns = (
    Object.keys(report).filter(
      (x) => x !== "vehicleA" && x !== "vehicleB",
    ) as Exclude<keyof AccidentReport, "vehicleA" | "vehicleB">[]
  ).filter((key) =>
    options?.commonSections ? options.commonSections[key] : false,
  );

  function cols<T>(...sections: (keyof AccidentReport)[]) {
    const set = new Set(
      ...sections.map((section) => {
        const x = report[section];

        if (typeof x !== "object") throw new Error("Only sections allowed");

        return Object.keys(x);
      }),
    );

    return Array.from(set) as (keyof T)[];
  }

  const vehicleColumns = cols<Vehicle>("vehicleA", "vehicleB");

  const vehicleSectionColumns = vehicleColumns.filter((key) =>
    options?.vehicleSections ? options.vehicleSections[key] : false,
  );

  const pageTwoColumns = cols<PageTwo>("pageTwoA", "pageTwoB");

  const pageTwoSectionColumns = pageTwoColumns.filter((key) =>
    options?.pageTwoSections ? options.pageTwoSections[key] : false,
  );

  return {
    ...commonSectionColumns.reduce(
      (acc, key) => {
        acc[key] = report[key] as Entry;
        return acc;
      },
      {} as { [key: string]: Entry },
    ),
    ...vehicleSectionColumns.reduce(
      (acc, key) => {
        acc.vehicleA[key] = report.vehicleA[key] as Entry;
        acc.vehicleB[key] = report.vehicleB[key] as Entry;
        return acc;
      },
      { vehicleA: {}, vehicleB: {} } as {
        vehicleA: { [key: string]: Entry };
        vehicleB: { [key: string]: Entry };
      },
    ),
    ...pageTwoSectionColumns.reduce(
      (acc, key) => {
        acc.pageTwoA[key] = report.pageTwoA[key] as Entry;
        acc.pageTwoB[key] = report.pageTwoA[key] as Entry;
        return acc;
      },
      { pageTwoA: {}, pageTwoB: {} } as {
        pageTwoA: { [key: string]: Entry };
        pageTwoB: { [key: string]: Entry };
      },
    ),
  };
}

function formatDateInputValue(value: Date | null) {
  return value ? new Date(value).toISOString().split("T")[0] : "";
}

function IncludeSections<T extends { [key: string]: boolean | undefined }>({
  title,
  sections,
  onChange,
}: {
  title: ReactNode;
  sections: T;
  onChange: Dispatch<SetStateAction<T>>;
}) {
  return (
    <fieldset>
      <legend>{title}</legend>
      <div className="ExportIncludeEntities">
        {Object.keys(sections)
          .map((name) => ({ name, value: sections[name] }))
          .map(({ name, value }) => (
            <div key={name}>
              <label>
                <input
                  type="checkbox"
                  name={name}
                  checked={value}
                  onChange={({ target: { checked: value } }) =>
                    onChange((options) => ({
                      ...options,
                      [name]: value,
                    }))
                  }
                />
                {name}
              </label>
            </div>
          ))}
      </div>
    </fieldset>
  );
}

const ExportOptionsFieldset = ({
  options,
  onChange,
}: {
  options: Partial<{ [key in ExportOption]: boolean | string }>;
  onChange: Dispatch<
    SetStateAction<Partial<{ [key in ExportOption]: boolean | string }>>
  >;
}) => (
  <fieldset>
    <legend>Options</legend>
    {EXPORT_OPTIONS.map(({ id: name, label, type }) => {
      switch (type) {
        case "checkbox":
          return (
            <div key={name}>
              <label>
                <input
                  type="checkbox"
                  name={name}
                  checked={
                    options[name] !== undefined
                      ? (options[name] as boolean)
                      : false
                  }
                  onChange={({ target: { checked: value } }) =>
                    onChange((options) => ({
                      ...options,
                      [name]: value,
                    }))
                  }
                />
                {label}
              </label>
            </div>
          );
        case "date":
          return (
            <div key={name}>
              <label htmlFor={name}>{label}</label>
              <input
                type="date"
                name={name}
                value={(options[name] || "").toString()}
                onChange={({ target: { value } }) =>
                  onChange((options) => ({ ...options, [name]: value }))
                }
              />
            </div>
          );
        case "number":
          return (
            <div key={name}>
              <label htmlFor={name}>{label}</label>
              <input
                type="number"
                name={name}
                value={(options[name] || "").toString()}
                onChange={({ target: { value } }) =>
                  onChange((options) => ({ ...options, [name]: value }))
                }
              />
            </div>
          );
        default:
          throw new Error(`Unknown option type ${type}`);
      }
    })}
  </fieldset>
);

enum StaticColumn {
  Id = "id",
  Created = "created",
}

type StaticColumnDefinition =
  | {
      column: StaticColumn;
    }
  | {
      key: string;
      calculated: (report: AccidentReport) => string | null | undefined;
    };

const STATIC_COLUMNS: StaticColumnDefinition[] = [
  { column: StaticColumn.Id },
  { column: StaticColumn.Created },
  {
    key: "Signed",
    calculated: (report: AccidentReport) =>
      report.signature &&
      report.signature.signatureStatus === SignatureStatus.Signed
        ? report.signature.signatureStatusUpdated
        : null,
  },
];

const Export = () => {
  const [exported, setExported] = useState<Date | null>(null);
  const [progress, setProgress] = useState(0);
  const [download, setDownload] = useState<{
    uri: string;
    rows: number;
    duration: number;
    size: number;
    type: string;
    filename: string;
  } | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [options, setOptions] = useState(
    EXPORT_OPTIONS.reduce(
      (defaults, o) => {
        defaults[o.id] = o.default;

        return defaults;
      },
      {} as Partial<{ [key in ExportOption]: string | boolean }>,
    ),
  );

  const [commonSections, setCommonSections] = useState({
    partyA: false,
    partyB: false,
    accidentDescription: false,
    accidentDrawing: false,
    signature: false,
    document: false,
  });

  const [vehicleSections, setVehicleSections] = useState<{
    [key in keyof Vehicle]: boolean;
  }>({
    vehicleData: false,
    trailerData: false,
    driverContactInfo: false,
    insureeContactInfo: false,
    driver: false,
    insurance: false,
    collisionTouch: false,
    damageDescription: false,
    comments: false,
    accidentQuestions: false,
    attachments: false,
    witness: false,
  });

  const [pageTwoSections, setPageTwoSections] = useState<{
    [key in keyof PageTwo]: boolean;
  }>({
    status: true,
    supplementalInfo: false,
    damagedPropertyOwner: false,
    accidentDescription: false,
    injuree: false,
    injureeContactInfo: false,
    document: false,
  });

  const { fetch } = useAdminClient<PaginationResult<AccidentReport>>();

  const fetchExport = useCallback(
    (cursor: string | null) =>
      fetch({
        path: `admin/reports?${queryString.stringify({
          cursor,
          from: options[ExportOption.From],
          to: options[ExportOption.To],
          limit: options[ExportOption.Limit],
        })}`,
      }),
    [fetch, options],
  );

  const exportData = useCallback(
    async function exportData() {
      setIsExporting(true);
      setProgress(0);
      const startTimestamp = new Date();
      let cursor: string | null = null;

      let data: { report: AccidentReport; data: Object }[] = [];

      do {
        setProgress((p) => p + 1);

        const result: PaginationResult<AccidentReport> | undefined =
          await fetchExport(cursor);

        if (result) {
          data = data.concat(
            result.items.map((report) => ({
              report: report,
              data: getExportData(report, {
                ...options,
                commonSections,
                vehicleSections,
                pageTwoSections,
              }),
            })),
          );
          cursor = result.cursor;
        } else {
          cursor = null;
        }
      } while (cursor);

      const flattened = data.map(({ data, report }) => ({
        flat: flattenObject(data),
        report,
      }));

      const staticKeys = STATIC_COLUMNS.map((x) =>
        "column" in x ? x.column : x.key,
      );

      const dataKeys = Object.keys(
        flattened.reduce(
          (collected, { flat }) => {
            for (const x of Object.keys(flat)) collected[x] = true;
            return collected;
          },
          {} as { [key: string]: boolean },
        ),
      )
        .filter((k) => !staticKeys.includes(k))
        .sort();

      console.log("data", data);

      const rows = [
        [...staticKeys, ...dataKeys],
        ...flattened.map(({ flat, report }) =>
          [
            ...STATIC_COLUMNS.map((column) => {
              return "calculated" in column
                ? column.calculated(report)
                : report[column.column];
            }),
            ...dataKeys.map((key) => flat[key]),
          ].map((value) => {
            if (value === null || value === undefined) return value;
            if (typeof value === "string")
              return `"${value.replace(/"/g, '""')}"`;
            return value.toString();
          }),
        ),
      ];

      const COLUMN_SEPARATOR = ";";

      const csv = rows.map((row) => row.join(COLUMN_SEPARATOR)).join("\n");

      const blob = new Blob([csv]);

      const uri = URL.createObjectURL(blob);

      setDownload({
        uri,
        rows: flattened.length,
        duration: +new Date() - +startTimestamp,
        size: blob.size,
        type: "text/csv",
        filename: `skademelding-export-${new Date().toISOString()}.csv`,
      });

      setIsExporting(false);
    },
    [fetchExport, commonSections, vehicleSections, pageTwoSections, options],
  );

  return (
    <div className="Export">
      <form>
        <ExportOptionsFieldset options={options} onChange={setOptions} />
        <IncludeSections
          title="Common Sections"
          sections={commonSections}
          onChange={setCommonSections}
        />
        <IncludeSections
          title="Vehicle Sections"
          sections={vehicleSections}
          onChange={setVehicleSections}
        />
        <IncludeSections
          title="Page Two Sections"
          sections={pageTwoSections}
          onChange={setPageTwoSections}
        />
        <div>
          <button
            type="button"
            onClick={() => {
              setExported(new Date());
              exportData();
            }}
            disabled={isExporting}
          >
            Export
          </button>
        </div>
      </form>
      {isExporting ? (
        <LoadingText text={`Downloading page ${progress}`} />
      ) : null}
      {download ? (
        <div>
          <span>
            [{exported?.toLocaleTimeString()}] Exported {download.rows} reports
            in {(download.duration / 1000).toFixed(2)}&nbsp;s.
          </span>
          <br />
          <a
            href={download.uri}
            download={download.filename}
            type={download.type}
          >
            Download exported data
          </a>{" "}
          (<Bytes bytes={download.size} />)
        </div>
      ) : null}
    </div>
  );
};

export default Export;
